import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  ListItem,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';

import { DragHandleIcon, Icon, IconOption, useDraggableContext } from '@components/base';
import { OrderBy, SortOrder } from '@models/OrderBy';

import OrderByFieldSelect from './OrderByFieldSelect';

type SortOrderRowProps = OrderBy & {
  handleDeleteRow: () => void;
  inset?: boolean;
  setOrderBy: Dispatch<SetStateAction<OrderBy[]>>;
};

const sortOrderIconMap: Record<SortOrder, IconOption> = {
  desc: 'sortDescending',
  asc: 'sortAscending',
};

const SortOrderRow: FC<SortOrderRowProps> = ({
  field,
  handleDeleteRow,
  id,
  inset,
  setOrderBy,
  sortOrder,
}) => {
  const { t } = useTranslation();
  const { draggableProvided: { dragHandleProps } = {} } = useDraggableContext();

  const handleChangeField = (field: string) => (value: string) =>
    setOrderBy(prev => prev.map(item => (item.id === id ? { ...item, [field]: value } : item)));

  return (
    <ListItem dense>
      {dragHandleProps && (
        <Box {...dragHandleProps} alignItems='center' display='flex' pr={1}>
          <DragHandleIcon />
        </Box>
      )}

      <ListItemText inset={!dragHandleProps && inset}>
        <Stack alignItems='center' direction='row' spacing={1}>
          <OrderByFieldSelect id={id} onSelect={handleChangeField('field')} value={field} />

          <ToggleButtonGroup
            aria-label={t('sort.order')}
            exclusive
            onChange={(_, value) => value && handleChangeField('sortOrder')(value)}
            size='small'
            value={sortOrder}
          >
            {Object.entries(sortOrderIconMap).map(([so, icon]) => (
              <ToggleButton aria-label={t('sort.order', { context: so })} key={so} value={so}>
                <Tooltip title={t('sort.order', { context: so })}>
                  <Icon name={icon} />
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Box ml={0.5} mr={-0.5}>
            <IconButton onClick={handleDeleteRow} size='small'>
              <Icon name='delete' />
            </IconButton>
          </Box>
        </Stack>
      </ListItemText>
    </ListItem>
  );
};

export default SortOrderRow;
